import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'

import { useAdminMutation } from '../api'
import { fromZonedInput } from '../zonedInput'
import type { AdminEvent } from '../types'
import { I18nField, NumberField, TextField } from './fields'
import {
  CurrencyField,
  LocalesField,
  TimezoneField,
  isValidTimeZone,
} from './editionFields'

/** Même règle que le serveur (`SLUG_PATTERN`) : le refus s'annonce avant l'envoi. */
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/

export function slugProblem(slug: string): boolean {
  return slug.length < 3 || slug.length > 120 || !SLUG_PATTERN.test(slug)
}

interface CreateBody {
  slug: string
  edition_year: number
  name_i18n: Record<string, string>
  starts_at: string
  currency: string
  default_locale: string
  locales: string[]
  timezone: string
}

/**
 * Une édition partie de rien.
 *
 * Rarement utile — on duplique d'ordinaire l'édition précédente, qui apporte
 * sa carte, ses billets et ses textes. Mais il faut bien pouvoir créer la
 * première, ou repartir à blanc quand le concept change.
 *
 * L'édition naît en brouillon : rien n'est visible du public tant qu'on ne
 * l'a pas publiée, et la publication a ses propres garde-fous.
 */
export function EditionCreateForm({
  eventId,
  onCreated,
  onCancel,
}: {
  eventId: string | undefined
  onCreated: (event: AdminEvent) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [slug, setSlug] = useState('')
  const [year, setYear] = useState<number | null>(new Date().getFullYear() + 1)
  const [name, setName] = useState<Record<string, string>>({})
  const [starts, setStarts] = useState('')
  const [currency, setCurrency] = useState('EUR')
  const [locales, setLocales] = useState<string[]>(['de', 'fr', 'en'])
  const [defaultLocale, setDefaultLocale] = useState('de')
  const [timezone, setTimezone] = useState('Europe/Berlin')

  const create = useAdminMutation<AdminEvent, CreateBody>(
    'post',
    () => '/admin/events',
    eventId,
    [['admin-editions'], ['public-event']],
  )

  const zone = timezone.trim()
  const zoneOk = isValidTimeZone(zone)
  // L'heure saisie n'a de sens que dans un fuseau valide : tant qu'il ne
  // l'est pas, on ne la convertit pas (Intl lèverait une exception).
  const startsIso = zoneOk ? fromZonedInput(starts, zone) : null

  // Ce qui manque, dit en clair — un bouton grisé sans raison fait chercher.
  const problems: string[] = []
  if (slugProblem(slug)) problems.push(t('admin.editions.problem.slug'))
  if (year === null || year < 2020 || year > 2100) problems.push(t('admin.editions.problem.year'))
  if (!name[defaultLocale]?.trim())
    problems.push(t('admin.editions.problem.name', { locale: defaultLocale.toUpperCase() }))
  if (!startsIso) problems.push(t('admin.editions.problem.starts'))
  if (!/^[A-Z]{3}$/.test(currency)) problems.push(t('admin.editions.problem.currency'))
  if (!zoneOk) problems.push(t('admin.editions.problem.timezone'))

  function submit() {
    if (problems.length > 0 || !startsIso || year === null) return
    // On n'envoie que les langues retenues : le serveur refuse un texte dans
    // une langue que l'édition ne déclare pas.
    const nameI18n = Object.fromEntries(
      Object.entries(name)
        .map(([code, value]) => [code, value.trim()] as const)
        .filter(([code, value]) => locales.includes(code) && value !== ''),
    )
    create.mutate(
      {
        slug,
        edition_year: year,
        name_i18n: nameI18n,
        starts_at: startsIso,
        currency,
        default_locale: defaultLocale,
        locales,
        timezone: zone,
      },
      { onSuccess: (created) => onCreated(created) },
    )
  }

  return (
    <div className="rounded-md border border-line-subtle bg-bg-base/40 p-4 sm:p-5">
      <h3 className="font-display text-heading-sm text-content-primary">
        {t('admin.editions.create.title')}
      </h3>
      <p className="mt-1 text-sm text-content-secondary">{t('admin.editions.create.intro')}</p>

      <div className="mt-4 grid gap-5 sm:grid-cols-2">
        <TextField
          label={t('admin.editions.slug')}
          hint={t('admin.editions.slugHint')}
          value={slug}
          onChange={(value) => setSlug(value.trim().toLowerCase())}
          placeholder="masked-night-2027"
        />
        <NumberField
          label={t('admin.editions.year')}
          value={year}
          min={2020}
          max={2100}
          onChange={setYear}
        />
        <div className="sm:col-span-2">
          <I18nField
            key={locales.join(',')}
            label={t('admin.event.name')}
            hint={t('admin.editions.nameHint', { locale: defaultLocale.toUpperCase() })}
            value={name}
            locales={locales}
            onChange={setName}
          />
        </div>
        <TimezoneField
          label={t('admin.event.timezone')}
          hint={t('admin.editions.create.timezoneHint')}
          value={timezone}
          onChange={setTimezone}
        />
        <TextField
          label={t('admin.event.starts')}
          hint={zoneOk ? t('admin.event.whenHint', { timezone: zone }) : t('admin.editions.problem.timezone')}
          type="datetime-local"
          value={starts}
          onChange={setStarts}
        />
        <CurrencyField
          label={t('admin.event.currency')}
          hint={t('admin.editions.create.currencyHint')}
          value={currency}
          onChange={setCurrency}
        />
        <div className="hidden sm:block" aria-hidden />
        <LocalesField
          locales={locales}
          defaultLocale={defaultLocale}
          onChange={(next) => {
            setLocales(next.locales)
            setDefaultLocale(next.default_locale)
          }}
        />
      </div>

      {problems.length > 0 ? (
        <ul className="mt-4 space-y-1 text-xs text-content-muted">
          {problems.map((problem) => (
            <li key={problem}>· {problem}</li>
          ))}
        </ul>
      ) : null}

      {create.error ? (
        <p className="mt-3 text-sm text-state-danger" role="alert">
          {create.error instanceof ApiError ? create.error.message : t('admin.editions.genericError')}
        </p>
      ) : null}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button
          size="sm"
          disabled={problems.length > 0 || create.isPending}
          onClick={submit}
        >
          {create.isPending ? t('state.loading') : t('admin.editions.create.submit')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('admin.cancel')}
        </Button>
      </div>
    </div>
  )
}
